import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { DateField } from "./DateField";
import { Plus, Trash2, ArrowRight, ArrowLeft, Building2, User, CalendarDays, Receipt } from "lucide-react";
import { US_STATES, STATE_TAX_RATES } from "../lib/states";
import { inputCls, labelCls, btnPrimary, btnGhost } from "../lib/ui";
import { money, computePaystub } from "../lib/paystub";

const Section = ({ icon: Icon, title, subtitle, children }) => (
  <div className="rounded-xl border border-[#D0E2FF] bg-white shadow-sm p-6 sm:p-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-[#F0F6FF] border border-[#D0E2FF] flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#2563EB]" />
      </div>
      <div>
        <h3 className="font-display text-lg font-semibold text-[#0F172A]">{title}</h3>
        {subtitle && <p className="text-sm text-[#64748B]">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

const Field = ({ label, children, span }) => (
  <div className={span === 2 ? "sm:col-span-2" : ""}>
    <Label className={`${labelCls} mb-1.5 block`}>{label}</Label>
    {children}
  </div>
);

export const PaystubForm = ({ form, setForm, onNext, onBack }) => {
  const [certified, setCertified] = useState(false);
  const san = (v) => v.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateEarning = (i, k, v) =>
    setForm((f) => {
      const earnings = f.earnings.map((e, idx) => (idx === i ? { ...e, [k]: v } : e));
      return { ...f, earnings };
    });
  const addEarning = () =>
    setForm((f) => ({ ...f, earnings: [...f.earnings, { description: "", payRate: "", hours: "" }] }));
  const removeEarning = (i) =>
    setForm((f) => ({ ...f, earnings: f.earnings.filter((_, idx) => idx !== i) }));

  const calc = computePaystub(form);
  const stateRate = form.state ? STATE_TAX_RATES[form.state] ?? 0 : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-[#0F172A]">
          Build your earnings statement
        </h2>
        <p className="text-[#475569] mt-1">Fill each section. Figures update live in the summary.</p>
      </div>

      {/* 1. Employee */}
      <Section icon={User} title="Employee Information" subtitle="Who the paystub is for">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="First Name">
            <Input data-testid="input-firstName" className={inputCls} value={form.firstName}
              onChange={(e) => update("firstName", e.target.value)} placeholder="Jordan" />
          </Field>
          <Field label="Last Name">
            <Input data-testid="input-lastName" className={inputCls} value={form.lastName}
              onChange={(e) => update("lastName", e.target.value)} placeholder="Rivera" />
          </Field>
          <Field label="State of Residence">
            <Select value={form.state} onValueChange={(v) => update("state", v)}>
              <SelectTrigger data-testid="select-state" className={inputCls}>
                <SelectValue placeholder="Select a state" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {US_STATES.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Last 4 of SSN">
            <Input data-testid="input-ssn" className={inputCls} inputMode="numeric" maxLength={4}
              value={form.ssnLast4}
              onChange={(e) => update("ssnLast4", e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="1234" />
          </Field>
          <Field label="Employee Address" span={2}>
            <Input data-testid="input-employeeAddress" className={inputCls} value={form.employeeAddress}
              onChange={(e) => update("employeeAddress", e.target.value)}
              placeholder="123 Main St, City, ST 00000" />
          </Field>
        </div>
      </Section>

      {/* 2. Employer */}
      <Section icon={Building2} title="Employer Information" subtitle="Company & payer details">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Company Name">
            <Input data-testid="input-companyName" className={inputCls} value={form.companyName}
              onChange={(e) => update("companyName", e.target.value)} placeholder="Acme Corp" />
          </Field>
          <Field label="Employer Number">
            <Input data-testid="input-employerNumber" className={inputCls} value={form.employerNumber}
              onChange={(e) => update("employerNumber", e.target.value)} placeholder="EMP-00123" />
          </Field>
          <Field label="Company Address" span={2}>
            <Input data-testid="input-companyAddress" className={inputCls} value={form.companyAddress}
              onChange={(e) => update("companyAddress", e.target.value)}
              placeholder="500 Market Ave, Suite 200, City, ST 00000" />
          </Field>
          <Field label="Department (optional)">
            <Input data-testid="input-department" className={inputCls} value={form.department}
              onChange={(e) => update("department", e.target.value)} placeholder="000 / 001 / 015" />
          </Field>
          <Field label="Bank Name (for direct deposit)">
            <Input data-testid="input-bankName" className={inputCls} value={form.bankName}
              onChange={(e) => update("bankName", e.target.value)} placeholder="First National Bank" />
          </Field>
        </div>
      </Section>

      {/* 3. Payroll */}
      <Section icon={CalendarDays} title="Payroll & Earnings" subtitle="Pay period, method & earnings lines">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <Field label="Pay Period Start">
            <DateField testId="input-payPeriodStart" value={form.payPeriodStart}
              onChange={(v) => update("payPeriodStart", v)} />
          </Field>
          <Field label="Pay Period End">
            <DateField testId="input-payPeriodEnd" value={form.payPeriodEnd}
              onChange={(v) => update("payPeriodEnd", v)} />
          </Field>
          <Field label="Paycheck Date *">
            <DateField testId="input-payDate" value={form.payDate} required
              onChange={(v) => update("payDate", v)} />
          </Field>
        </div>

        <Field label="Payment Delivery Method">
          <RadioGroup
            data-testid="radio-paymentMethod"
            value={form.paymentMethod}
            onValueChange={(v) => update("paymentMethod", v)}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1"
          >
            {[
              { v: "physical", label: "Physical Check" },
              { v: "direct_deposit", label: "Direct Deposit Advice" },
            ].map((o) => (
              <label
                key={o.v}
                data-testid={`payment-${o.v}`}
                className={`flex items-center gap-3 rounded-lg border p-4 cursor-pointer transition-colors ${
                  form.paymentMethod === o.v
                    ? "border-[#60A5FA] bg-[#F0F6FF]"
                    : "border-[#D0E2FF] hover:border-[#A6C8FF]"
                }`}
              >
                <RadioGroupItem value={o.v} />
                <span className="text-sm font-medium text-[#0F172A]">{o.label}</span>
              </label>
            ))}
          </RadioGroup>
        </Field>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <Label className={labelCls}>Earnings Lines</Label>
            <Button type="button" variant="outline" size="sm" data-testid="add-earning-button"
              onClick={addEarning} className={btnGhost}>
              <Plus className="w-4 h-4 mr-1" /> Add line
            </Button>
          </div>
          <div className="space-y-3">
            {form.earnings.map((e, i) => (
              <div key={i} className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-12 sm:col-span-5">
                  <Label className="text-[11px] text-[#64748B]">Description</Label>
                  <Input data-testid={`earning-desc-${i}`} className={inputCls} value={e.description}
                    onChange={(ev) => updateEarning(i, "description", ev.target.value)} placeholder="Regular" />
                </div>
                <div className="col-span-5 sm:col-span-3">
                  <Label className="text-[11px] text-[#64748B]">Pay Rate ($/hr)</Label>
                  <Input data-testid={`earning-rate-${i}`} className={inputCls} inputMode="decimal" value={e.payRate}
                    onChange={(ev) => updateEarning(i, "payRate", san(ev.target.value))} placeholder="15.50" />
                </div>
                <div className="col-span-5 sm:col-span-3">
                  <Label className="text-[11px] text-[#64748B]">Hours</Label>
                  <Input data-testid={`earning-hours-${i}`} className={inputCls} inputMode="decimal" value={e.hours}
                    onChange={(ev) => updateEarning(i, "hours", san(ev.target.value))} placeholder="40" />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  {form.earnings.length > 1 && (
                    <Button type="button" variant="ghost" size="icon" data-testid={`remove-earning-${i}`}
                      onClick={() => removeEarning(i)} className="text-[#EF4444] hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <Field label="Pay periods year-to-date">
              <Input data-testid="input-payPeriodsYTD" className={inputCls} inputMode="numeric"
                value={form.payPeriodsYTD}
                onChange={(e) => update("payPeriodsYTD", e.target.value.replace(/\D/g, ""))} placeholder="1" />
            </Field>
            <div className="flex items-end">
              <div className="w-full rounded-lg bg-[#F8FAFC] border border-[#D0E2FF] px-4 py-3 flex items-center justify-between">
                <span className="text-sm text-[#64748B]">Gross (current)</span>
                <span className="font-mono-num font-semibold text-[#0F172A]" data-testid="gross-preview">
                  {money(calc.grossCurrent)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 4. Deductions & Taxes */}
      <Section icon={Receipt} title="Deductions & Taxes" subtitle="Withholdings adjust to your selected state">
        <div className="rounded-lg bg-[#F0F6FF] border border-[#D0E2FF] px-4 py-3 mb-6 text-sm text-[#1E40AF]">
          {form.state
            ? <>Estimated <b>{form.state}</b> state withholding rate: <b>{stateRate}%</b>. Leave overrides blank to auto-calculate, or enter a custom amount.</>
            : <>Select a state above to auto-estimate state withholding. You can always override amounts manually.</>}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <Field label="Federal Withholding (override)">
            <Input data-testid="input-federalWithholding" className={inputCls} inputMode="decimal"
              value={form.federalWithholding}
              onChange={(e) => update("federalWithholding", san(e.target.value))}
              placeholder={`Auto: ${money(calc.taxes[0].current)}`} />
          </Field>
          <Field label="State Withholding (override)">
            <Input data-testid="input-stateWithholding" className={inputCls} inputMode="decimal"
              value={form.stateWithholding}
              onChange={(e) => update("stateWithholding", san(e.target.value))}
              placeholder={`Auto: ${money(calc.taxes[3].current)}`} />
          </Field>
          <Field label="Medical Insurance">
            <Input data-testid="input-medicalInsurance" className={inputCls} inputMode="decimal"
              value={form.medicalInsurance}
              onChange={(e) => update("medicalInsurance", san(e.target.value))} placeholder="0.00" />
          </Field>
          <Field label="Benefits / 401(k)">
            <Input data-testid="input-benefits401k" className={inputCls} inputMode="decimal"
              value={form.benefits401k}
              onChange={(e) => update("benefits401k", san(e.target.value))} placeholder="0.00" />
          </Field>
        </div>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="rounded-lg border border-[#D0E2FF] p-4">
            <div className="text-xs text-[#64748B]">Gross</div>
            <div className="font-mono-num font-semibold text-[#0F172A]">{money(calc.grossCurrent)}</div>
          </div>
          <div className="rounded-lg border border-[#D0E2FF] p-4">
            <div className="text-xs text-[#64748B]">Taxes + Deductions</div>
            <div className="font-mono-num font-semibold text-[#0F172A]">
              {money(calc.taxes.reduce((s, t) => s + t.current, 0) + calc.deductions.reduce((s, d) => s + d.current, 0))}
            </div>
          </div>
          <div className="rounded-lg border-2 border-[#60A5FA] bg-[#F0F6FF] p-4 col-span-2 sm:col-span-1">
            <div className="text-xs text-[#1E40AF]">Net Pay</div>
            <div className="font-mono-num font-bold text-[#1E40AF]" data-testid="net-preview">{money(calc.netCurrent)}</div>
          </div>
        </div>
      </Section>

      <div className="rounded-xl border border-[#D0E2FF] bg-[#F8FAFC] p-5 sm:p-6">
        <label htmlFor="certify-checkbox" className="flex items-start gap-3 cursor-pointer">
          <Checkbox
            id="certify-checkbox"
            data-testid="certify-checkbox"
            checked={certified}
            onCheckedChange={(v) => setCertified(v === true)}
            className="mt-0.5 border-[#A6C8FF] data-[state=checked]:bg-[#60A5FA] data-[state=checked]:border-[#60A5FA]"
          />
          <span className="text-sm leading-relaxed text-[#475569]">
            I confirm the information I enter is accurate and I will not use this document to misrepresent my
            income to any landlord, lender, or other party.
          </span>
        </label>
      </div>

      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onBack} data-testid="form-back-button" className={btnGhost}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!certified}
          data-testid="form-continue-button"
          className={`${btnPrimary} rounded-full px-8 py-6 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#60A5FA]`}
        >
          Continue to Payment <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </motion.div>
  );
};
